import React, { useState } from 'react';
import { useAuth } from '../../authContext';

// Custom Hook
import { useTasks } from '../tasks/useTasks';

// Lokale Komponenten
import TaskForm from '../tasks/TaskForm';
import ContactSidebar from '../tasks/ContactSidebar';
import CompanySidebar from '../tasks/CompanySidebar';

// Shared Components
import Modal from '../shared/Modal';
import StatusBadge from '../shared/StatusBadge';
import SearchBar from '../shared/SearchBar';
import DataTable from '../shared/DataTable';
import ConfirmModal from '../shared/ConfirmModal';

// Formular für neuen Ansprechpartner (wiederverwendet aus Contacts.jsx)
import ContactForm from '../contacts/ContactForm';

const Tasks = () => {
  // Authentifizierung und Rolle
  const { role, userId } = useAuth();

  // Custom Hook nutzen
  const {
    tasks,
    contacts,
    companies,
    loading,
    error,
    addTask,
    editTask: editTaskApi, // Umbenennung, um Namenskonflikt zu vermeiden
    deleteTask,
    toggleTaskStatus,
    addContactForTask,
    formatDate,
    getContactsForCompany,
  } = useTasks();

  // Modal-State
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // State für ausgewählte Task-IDs und Daten
  const [selectedTaskForToggle, setSelectedTaskForToggle] = useState(null);
  const [selectedTaskForDelete, setSelectedTaskForDelete] = useState(null);
  const [newTask, setNewTask] = useState({
    Title: '',
    Description: '',
    ContactId: '',
    DueDate: '',
    Status: 'TODO',
  });
  const [editingTask, setEditingTask] = useState(null);
  const [viewTask, setViewTask] = useState(null);

  // State für Seitenleisten
  const [showContactSidebar, setShowContactSidebar] = useState(false);
  const [showCompanySidebar, setShowCompanySidebar] = useState(false);
  const [selectedContactForSidebar, setSelectedContactForSidebar] = useState(null);
  const [selectedCompanyForSidebar, setSelectedCompanyForSidebar] = useState(null);

  // State für neuen Ansprechpartner
  const [newContactForTask, setNewContactForTask] = useState({
    companyId: '',
    firstName: '',
    lastName: '',
    position: '',
    email: '',
    phone: '',
    remarks: '',
    isDefault: false,
  });

  // Filter-State
  const [searchTitle, setSearchTitle] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [statusFilter, setStatusFilter] = useState('TODO');
  const [userFilter, setUserFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [dueDateFilter, setDueDateFilter] = useState('heute'); // NEU: Fälligkeitsfilter

  // Aktuelles Datum für den Vergleich
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Gefilterte Aufgaben
  const filteredTasks = tasks.filter((task) => {
    const titleMatch = (task.taskTitle || task.Title || '').toLowerCase().includes(searchTitle.toLowerCase());
    const statusMatch =
      statusFilter === 'all' ||
      (statusFilter === 'TODO' && (task.taskStatus || task.Status) === 'TODO') ||
      (statusFilter === 'DONE' && (task.taskStatus || task.Status) === 'DONE');
    const userMatch =
      userFilter === 'all' ||
      (userFilter === 'me' && task.userId === userId) ||
      (userFilter === 'others' && task.userId !== userId);
    const companyMatch =
      searchCompany === '' ||
      (task.companyName || '').toLowerCase().includes(searchCompany.toLowerCase());

    // NEU: Filter für Fälligkeit
    const dueDate = task.taskDueDate ? new Date(task.taskDueDate) : null;
    let dueDateMatch = true;

    if (dueDateFilter === 'heute') {
      const taskDate = new Date(dueDate);
      taskDate.setHours(0, 0, 0, 0);
      dueDateMatch = taskDate.getTime() === today.getTime();
    } else if (dueDateFilter === 'überfällig') {
      dueDateMatch = dueDate < today && (task.taskStatus || task.Status) === 'TODO';
    }
    // "alle" zeigt alle Aufgaben an (dueDateMatch bleibt true)

    return titleMatch && statusMatch && userMatch && companyMatch && dueDateMatch;
  });

  // Seitenleisten-Funktionen
  const openContactSidebar = (contactId) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (contact) {
      setSelectedContactForSidebar(contact);
      setShowContactSidebar(true);
    }
  };

  const openCompanySidebar = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    if (company) {
      setSelectedCompanyForSidebar(company);
      setShowCompanySidebar(true);
    }
  };

  const closeSidebars = () => {
    setShowContactSidebar(false);
    setShowCompanySidebar(false);
  };

  // Modal-Funktionen
  const openViewModalWithSidebars = (task) => {
    setViewTask(task);
    setShowViewModal(true);
    openContactSidebar(task.contactId);
    const contact = contacts.find((c) => c.id === task.contactId);
    if (contact) {
      openCompanySidebar(contact.companyId);
    }
  };

  const openEditModalWithSidebars = (task) => {
    const contact = contacts.find((c) => c.id === task.contactId);
    setEditingTask({
      Id: task.taskId,
      Title: task.taskTitle || task.Title,
      Description: task.taskDescription || task.Description,
      ContactId: task.contactId.toString(),
      DueDate: task.taskDueDate || task.DueDate,
      UserId: task.userId,
      Status: task.taskStatus || task.Status,
      CreatedAt: task.taskCreatedAt || task.CreatedAt,
      companyId: contact ? contact.companyId : null,
    });
    setShowEditModal(true);
    openContactSidebar(task.contactId);
    if (contact) {
      openCompanySidebar(contact.companyId);
    }
  };

  const openToggleStatusModal = (taskId, currentStatus) => {
    if (role !== 'Admin' && currentStatus !== 'TODO') {
      setErrorModalMessage('Nur Admins können den Status von Aufgaben ändern.');
      setShowErrorModal(true);
      return;
    }
    setSelectedTaskForToggle({ id: taskId, currentStatus });
    setShowToggleStatusModal(true);
  };

  const handleDeleteTask = (taskId) => {
    setSelectedTaskForDelete(taskId);
    setShowDeleteModal(true);
  };

  // Handler für Formulare
  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await addTask(newTask);
      setShowAddModal(false);
      setNewTask({
        Title: '',
        Description: '',
        ContactId: '',
        DueDate: '',
        Status: 'TODO',
      });
    } catch (err) {
      setErrorModalMessage('Fehler beim Erstellen der Aufgabe.');
      setShowErrorModal(true);
      console.error(err);
    }
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    try {
      if (!editingTask.Id) {
        // Kopierte Aufgabe: Neue Aufgabe erstellen
        await addTask({
          Title: editingTask.Title,
          Description: editingTask.Description,
          ContactId: editingTask.ContactId,
          DueDate: editingTask.DueDate,
          Status: 'TODO',
        });
      } else {
        // Bestehende Aufgabe bearbeiten
        await editTaskApi(editingTask.Id, {
          Title: editingTask.Title,
          Description: editingTask.Description,
          ContactId: editingTask.ContactId,
          DueDate: editingTask.DueDate,
          UserId: editingTask.UserId,
          CreatedAt: editingTask.CreatedAt,
          Status: editingTask.Status,
        });
      }
      setShowEditModal(false);
      closeSidebars();
    } catch (err) {
      setErrorModalMessage(err.response?.data?.message || 'Fehler beim Speichern der Aufgabe.');
      setShowErrorModal(true);
      console.error(err);
    }
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    try {
      await toggleTaskStatus(taskId, currentStatus);
      setShowToggleStatusModal(false);
    } catch (err) {
      setErrorModalMessage(err.message || 'Fehler beim Ändern des Status.');
      setShowErrorModal(true);
      setShowToggleStatusModal(false);
    }
  };

  const confirmDeleteTask = async () => {
    if (!selectedTaskForDelete) return;
    try {
      await deleteTask(selectedTaskForDelete);
      setShowDeleteModal(false);
      setSelectedTaskForDelete(null);
    } catch (err) {
      setErrorModalMessage('Fehler beim Löschen der Aufgabe.');
      setShowErrorModal(true);
      console.error(err);
      setShowDeleteModal(false);
      setSelectedTaskForDelete(null);
    }
  };

  const handleCopyTask = (task) => {
    const contact = contacts.find((c) => c.id === task.contactId);
    const companyId = contact ? contact.companyId : null;

    setEditingTask({
      Id: null,
      Title: task.taskTitle || task.Title,
      Description: task.taskDescription || task.Description,
      ContactId: task.contactId.toString(),
      DueDate: '',
      UserId: userId,
      Status: 'TODO',
      CreatedAt: new Date().toISOString(),
      companyId: companyId,
    });
    setShowEditModal(true);
    openContactSidebar(task.contactId);
    if (contact) {
      openCompanySidebar(contact.companyId);
    }
  };

  const handleOpenAddContactModal = (companyId) => {
    setNewContactForTask({
      companyId: companyId.toString(),
      firstName: '',
      lastName: '',
      position: '',
      email: '',
      phone: '',
      remarks: '',
      isDefault: false,
    });
    setShowAddContactModal(true);
  };

  const handleAddContactForTask = async (e) => {
    e.preventDefault();
    try {
      await addContactForTask(newContactForTask);
      setShowAddContactModal(false);
      setNewContactForTask({
        companyId: '',
        firstName: '',
        lastName: '',
        position: '',
        email: '',
        phone: '',
        remarks: '',
        isDefault: false,
      });
    } catch (err) {
      setErrorModalMessage('Fehler beim Erstellen des Ansprechpartners.');
      setShowErrorModal(true);
      console.error(err);
    }
  };

  // Suchfelder für SearchBar
  const searchFields = [
    { type: 'text', placeholder: 'Suche nach Titel...', value: searchTitle, onChange: setSearchTitle },
    {
      type: 'select',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'Alle Status' },
        { value: 'TODO', label: 'Offen' },
        { value: 'DONE', label: 'Erledigt' },
      ],
    },
    {
      type: 'select',
      value: userFilter,
      onChange: setUserFilter,
      options: [
        { value: 'all', label: 'Alle Nutzer' },
        { value: 'me', label: 'Meine Aufgaben' },
        { value: 'others', label: 'Andere Nutzer' },
      ],
    },
    {
      type: 'select',
      value: companyFilter,
      onChange: setCompanyFilter,
      options: [
        { value: 'all', label: 'Alle Unternehmen' },
        ...companies.map((company) => ({
          value: company.id,
          label: company.name,
        })),
      ],
    },
  ];

  // Tabellen-Spalten für DataTable
  const columns = [
    { key: 'taskTitle', header: 'Titel' },
    { key: 'taskDescription', header: 'Beschreibung', render: (task) => task.taskDescription || '–' },
    {
      key: 'contact',
      header: 'Kontakt',
      render: (task) => `${task.contactFirstName || ''} ${task.contactLastName || ''}`,
    },
    {
      key: 'companyName',
      header: 'Unternehmen',
      render: (task) => (
        <>
          {task.companyName || '–'}
          <button
            className="action-button add-contact"
            onClick={() => {
              const contact = contacts.find((c) => c.id === task.contactId);
              if (contact) {
                handleOpenAddContactModal(contact.companyId);
              }
            }}
            title="Neuer Ansprechpartner für dieses Unternehmen"
          >
            👤
          </button>
        </>
      ),
    },
    { key: 'taskDueDate', header: 'Fällig am', render: (task) => formatDate(task.taskDueDate) },
    {
      key: 'taskStatus',
      header: 'Status',
      render: (task) => (
        <StatusBadge
          value={task.taskStatus === 'DONE'}
          onClick={() => openToggleStatusModal(task.taskId, task.taskStatus)}
          type="task"
        />
      ),
    },
    { key: 'taskCreatedAt', header: 'Erstellt am', render: (task) => formatDate(task.taskCreatedAt) },
    {
      key: 'actions',
      header: 'Aktionen',
      render: (task) => (
        <div style={{ display: 'flex', gap: '5px' }}>
          <button className="action-button view" onClick={() => openViewModalWithSidebars(task)} title="Anzeigen">
            👁️
          </button>
          <button className="action-button edit" onClick={() => openEditModalWithSidebars(task)} title="Bearbeiten">
            ✏️
          </button>
          <button className="action-button copy" onClick={() => handleCopyTask(task)} title="Kopieren">
            📋
          </button>
          <button className="action-button delete" onClick={() => handleDeleteTask(task.taskId)} title="Löschen">
            🗑️
          </button>
        </div>
      ),
    },
  ];

  // Funktion zur Bestimmung, ob eine Aufgabe überfällig ist
  const isTaskOverdue = (task) => {
    return (
      (task.taskStatus || task.Status) === 'TODO' &&
      task.taskDueDate &&
      new Date(task.taskDueDate) < new Date(new Date().toDateString())
    );
  };

  // --- Render ---
  return (
    <div className="tasks-tab">
      {/* Seitenleisten */}
      {showContactSidebar && selectedContactForSidebar && (
        <ContactSidebar
          contact={selectedContactForSidebar}
          companies={companies}
          onClose={() => setShowContactSidebar(false)}
        />
      )}

      {showCompanySidebar && selectedCompanyForSidebar && (
        <CompanySidebar
          company={selectedCompanyForSidebar}
          onClose={() => setShowCompanySidebar(false)}
        />
      )}

      {/* Toolbar mit Suchfeldern */}
      <div className="toolbar">
        <div className="search-filters">
          {/* Suche nach Titel (manuelles Eingabefeld) */}
          <input
            type="text"
            placeholder="Suche nach Titel..."
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            className="search-input"
          />
          {/* Suche nach Unternehmen (manuelles Eingabefeld) */}
          <input
            type="text"
            placeholder="Suche nach Unternehmen..."
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
            className="search-input"
          />
          {/* SearchBar für Status und Nutzer */}
          <SearchBar
            fields={[
              {
                type: 'select',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'Alle Status' },
                  { value: 'TODO', label: 'Offen' },
                  { value: 'DONE', label: 'Erledigt' },
                ],
              },
              {
                type: 'select',
                value: userFilter,
                onChange: setUserFilter,
                options: [
                  { value: 'all', label: 'Alle Nutzer' },
                  { value: 'me', label: 'Meine Aufgaben' },
                  { value: 'others', label: 'Andere Nutzer' },
                ],
              },
              // NEU: Auswahlbox für Fälligkeit
              {
                type: 'select',
                value: dueDateFilter,
                onChange: setDueDateFilter,
                options: [
                  { value: 'heute', label: 'Heute' },
                  { value: 'alle', label: 'Alle' },
                  { value: 'überfällig', label: 'Überfällig' },
                ],
              },
            ]}
          />
        </div>
        <button className="add-button" onClick={() => setShowAddModal(true)}>
          ➕ Neue Aufgabe
        </button>
      </div>

      {/* Tabelle oder Lade-/Fehlermeldung */}
      {loading ? (
        <p>Lade Aufgaben...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <DataTable
          columns={columns}
          data={filteredTasks}
          keyField="taskId"
          rowClassName={(task) => (isTaskOverdue(task) ? 'overdue-row' : '')}
        />
      )}

      {/* Modal: Neue Aufgabe */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Neue Aufgabe"
        footer={
          <div className="modal-actions">
            <button type="submit" className="save-button" form="addTaskForm">
              Speichern
            </button>
            <button type="button" className="cancel-button" onClick={() => setShowAddModal(false)}>
              Abbrechen
            </button>
          </div>
        }
      >
        <form id="addTaskForm" onSubmit={handleAddTask}>
          <TaskForm
            data={newTask}
            onChange={setNewTask}
            contacts={contacts}
            companies={companies}
          />
        </form>
      </Modal>

      {/* Modal: Aufgabe bearbeiten */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          closeSidebars();
        }}
        title={editingTask?.Id ? 'Aufgabe bearbeiten' : 'Aufgabe kopieren'}
        footer={
          <div className="modal-actions">
            <button type="submit" className="save-button" form="editTaskForm">
              Speichern
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setShowEditModal(false);
                closeSidebars();
              }}
            >
              Abbrechen
            </button>
          </div>
        }
      >
        {editingTask && (
          <form id="editTaskForm" onSubmit={handleEditTask}>
            <TaskForm
              data={editingTask}
              onChange={setEditingTask}
              contacts={contacts}
              companies={companies}
              isEditMode={true}
              selectedCompanyId={editingTask.companyId}
            />
          </form>
        )}
      </Modal>

      {/* Modal: Aufgabe anzeigen */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          closeSidebars();
        }}
        title={viewTask?.taskTitle || ''}
        footer={
          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setShowViewModal(false);
                closeSidebars();
              }}
            >
              Schließen
            </button>
          </div>
        }
      >
        {viewTask && (
          <div className="view-modal-content">
            <p><strong>Beschreibung:</strong> {viewTask.taskDescription || 'Keine'}</p>
            <p><strong>Kontakt:</strong> {viewTask.contactFirstName} {viewTask.contactLastName}</p>
            <p><strong>Unternehmen:</strong> {viewTask.companyName || 'Unbekannt'}</p>
            <p><strong>Fällig am:</strong> {formatDate(viewTask.taskDueDate)}</p>
            <p><strong>Status:</strong> {viewTask.taskStatus === 'DONE' ? 'Erledigt' : 'Offen'}</p>
            <p><strong>Erstellt am:</strong> {formatDate(viewTask.taskCreatedAt)}</p>
            <p><strong>Erledigt am:</strong> {formatDate(viewTask.completionDate)}</p>
            <p><strong>Zugewiesen an:</strong> {viewTask.userUsername || `Nutzer #${viewTask.userId}`}</p>
          </div>
        )}
      </Modal>

      {/* Modal: Status ändern */}
      <ConfirmModal
        isOpen={showToggleStatusModal}
        onClose={() => setShowToggleStatusModal(false)}
        onConfirm={() => handleToggleStatus(selectedTaskForToggle.id, selectedTaskForToggle.currentStatus)}
        title="Status ändern"
        message={
          <>
            Möchten Sie den Status der Aufgabe <strong>{tasks.find((t) => t.taskId === selectedTaskForToggle?.id)?.taskTitle}</strong>
            wirklich auf <strong>{selectedTaskForToggle?.currentStatus === 'TODO' ? 'Erledigt' : 'Offen'}</strong> ändern?
          </>
        }
        confirmText="Ja, ändern"
      />

      {/* Modal: Aufgabe löschen */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTaskForDelete(null);
        }}
        onConfirm={confirmDeleteTask}
        title="Aufgabe löschen"
        message={
          <>
            Möchten Sie die Aufgabe <strong>{tasks.find((t) => t.taskId === selectedTaskForDelete)?.taskTitle}</strong>
            wirklich löschen?
          </>
        }
        confirmText="Ja, löschen"
      />

      {/* Modal: Neuer Ansprechpartner */}
      <Modal
        isOpen={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        title="Neuer Ansprechpartner"
        footer={
          <div className="modal-actions">
            <button type="submit" className="save-button" form="addContactForm">
              Speichern
            </button>
            <button type="button" className="cancel-button" onClick={() => setShowAddContactModal(false)}>
              Abbrechen
            </button>
          </div>
        }
      >
        <form id="addContactForm" onSubmit={handleAddContactForTask}>
          <ContactForm
            data={newContactForTask}
            onChange={setNewContactForTask}
            companies={companies}
            isEditMode={false}
          />
        </form>
      </Modal>

      {/* Modal: Fehlermeldung */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Fehler"
        footer={
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setShowErrorModal(false)}>
              Schließen
            </button>
          </div>
        }
      >
        <p>{errorModalMessage}</p>
      </Modal>
    </div>
  );
};

export default Tasks;